import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { uniqueTopic } from '@/lib/realtime';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { getRewardfulReferral } from '@/utils/rewardful';

export interface SubscriptionStatus {
  tier: "free" | "standard" | "enterprise";
  active: boolean;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

export function useSubscription() {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [currentTier, setCurrentTier] = useState<'free' | 'basic' | 'standard' | 'enterprise'>('free');
  const [hasActiveUserSubscription, setHasActiveUserSubscription] = useState(false);

  const fetchAndSetCurrentTier = useCallback(async () => {
    if (!user) {
      setCurrentTier('free');
      setHasActiveUserSubscription(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('plan_id, status')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        setCurrentTier('free');
        setHasActiveUserSubscription(false);
        return;
      }
      if (data && data.status === 'active' && data.plan_id) {
        setCurrentTier(data.plan_id as 'free' | 'basic' | 'standard' | 'enterprise');
        setHasActiveUserSubscription(true);
      } else {
        setCurrentTier('free');
        setHasActiveUserSubscription(false);
      }
    } catch (err) {
      setCurrentTier('free');
      setHasActiveUserSubscription(false);
    }
  }, [user]);

  const subscribe = async (promoCode?: string, tier?: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to subscribe to a plan.",
        variant: "destructive",
      });
      return null;
    }
    setLoading(true);
    try {
      const referral = getRewardfulReferral();
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          tier: tier || "standard",
          returnUrl: window.location.origin + '/purchase',
          referral,
          promoCode: promoCode && promoCode.trim() !== "" ? promoCode.trim() : undefined
        },
        headers: {
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.url) {
        window.location.href = data.url;
        return data;
      }
      throw new Error('No checkout URL returned');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast({
        title: "Subscription Error",
        description: error instanceof Error ? error.message : "Failed to create subscription",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createCustomerPortal = async () => {
    if (!user) {
      toast({ title: "Authentication required", variant: "destructive" });
      return null;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-customer-portal', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
      });

      if (error) throw new Error(error.message);
      if (data?.url) {
        window.location.href = data.url;
        return data.url;
      }
      throw new Error('No portal URL returned');
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const testSubscriptionSetup = async () => {
    if (!user) return null;
    try {
      console.log("Testing subscription setup...");
      const { data, error } = await supabase.functions.invoke('test-env', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
      });
      
      console.log("Test env result:", { data, error });
      
      if (error) {
        console.error("Test env error:", error);
        return null;
      }
      
      return data;
    } catch (err) {
      console.error("Test function error:", err);
      return null;
    }
  };

  const updatePlan = async (targetPlan: 'basic' | 'standard') => {
    if (!user) {
      toast({ title: "Authentication required", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-subscription-plan', {
        body: { target_plan_id: targetPlan },
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
      });

      if (error) {
        throw new Error(error.message || `Failed to update to ${targetPlan} plan.`);
      }
      
      toast({ 
        title: "Success", 
        description: data?.message || `Your subscription has been updated to ${targetPlan}.` 
      });
      await fetchAndSetCurrentTier();
    } catch (error: any) {
      toast({ 
        title: "Update Failed", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelPlan = async () => {
    if (!user) {
      toast({ title: "Authentication required", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { data: userSubscription, error: userSubscriptionError } = await supabase
        .from("user_subscriptions")
        .select("purchase_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .single();

      if (userSubscriptionError || !userSubscription) {
        throw new Error("Could not find active subscription to cancel.");
      }

      const { data: purchase, error: purchaseError } = await supabase
        .from("purchases")
        .select("stripe_subscription_id")
        .eq("id", userSubscription.purchase_id)
        .single();

      if (purchaseError || !purchase?.stripe_subscription_id) {
        throw new Error("Could not find Stripe subscription ID for cancellation.");
      }

      const subscriptionId = purchase.stripe_subscription_id;
      console.log("Cancelling subscription:", subscriptionId);

      // ✅ Add Authorization header
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: { subscriptionId },
        headers: {
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
      });

      if (error) throw new Error(error.message);

      console.log("Response from cancel-subscription:", data);

      toast({
        title: "Subscription Cancelled",
        description: data?.message || "You are now on the Free plan."
      });

      await fetchAndSetCurrentTier();
    } catch (error: any) {
      console.error("Error in cancelPlan:", error);
      toast({
        title: "Cancellation Failed",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(uniqueTopic('user-subscriptions-realtime'))
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_subscriptions', filter: `user_id=eq.${user.id}` },
        () => { fetchAndSetCurrentTier(); }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchAndSetCurrentTier]);

  useEffect(() => {
    fetchAndSetCurrentTier();
  }, [fetchAndSetCurrentTier]);

  return {
    subscribe,
    loading,
    currentTier,
    hasActiveUserSubscription,
    fetchAndSetCurrentTier,
    updatePlan,
    cancelPlan,
    createCustomerPortal,
  };
}